import { Loader2, Save, X } from 'lucide-react';
import React from 'react';
import { type Control, type FieldValues, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { FormFieldSchema } from '../../custom-fields';
import { FormRenderer } from '../../custom-fields/renderers/form-renderer';
import {
  extractFileAssociations,
  getCustomFieldDefaults,
  mapSchemaToFields,
  prepareContactSubmission,
} from '../../custom-fields/utils';
import { useFileUpload } from '../../hooks/useFileUpload';
import { type RouterOutputs, trpc, trpcClient } from '../../lib/trpc';
import { Button } from '../../ui/button';

export type ContactDetail = NonNullable<RouterOutputs['contacts']['getById']>;

export interface ContactCustomFieldsProps {
  contact: ContactDetail;
  variant?: 'readonly' | 'form';
  onSuccess?: () => void;
  onCancel?: () => void;
  isReadOnly?: boolean;
}

export function ContactCustomFields({
  contact,
  variant = 'readonly',
  onSuccess,
  onCancel,
  isReadOnly = false,
}: ContactCustomFieldsProps) {
  const [isEditing, setIsEditing] = React.useState(variant === 'form');
  const utils = trpc.useUtils();
  const { uploadAll } = useFileUpload();

  const handleUploadFile = async (file: File, category?: string) => {
    const ids = await uploadAll([file], category);
    if (ids.length === 0) throw new Error('Upload failed');
    return ids[0];
  };

  const handleGetFile = async (fileId: number) => {
    const file = await trpcClient.files.getById.query({ id: fileId });
    if (!file) throw new Error('File not found');

    let viewUrl = file.publicUrl || undefined;
    if (!viewUrl) {
      viewUrl = await trpcClient.files.getViewUrl.query({ id: fileId });
    }

    return {
      fileName: file.fileName,
      fileType: file.fileType,
      viewUrl,
    };
  };

  const renameMutation = trpc.files.update.useMutation({
    onSuccess: (_, variables) => {
      if (contact?.id) {
        utils.contacts.getFiles.invalidate({ contactId: contact.id });
      }
      utils.files.getById.invalidate({ id: variables.id });
      toast.success('File renamed');
    },
  });

  const handleRenameFile = async (fileId: number, newName: string) => {
    await renameMutation.mutateAsync({ id: fileId, label: newName });
  };

  const { data: contactType, isLoading: isLoadingType } = trpc.contactTypes.getById.useQuery(
    { id: contact.typeId ?? 0 },
    {
      enabled: !!contact.typeId && !contact.contactType,
      initialData: (contact.contactType ??
        undefined) as unknown as RouterOutputs['contactTypes']['getById'],
    }
  );

  const updateMutation = trpc.contacts.update.useMutation({
    onSuccess: () => {
      toast.success('Attributes updated');
      utils.contacts.search.invalidate();
      if (contact?.id) {
        utils.contacts.getById.invalidate({ id: contact.id });
      }
      setIsEditing(false);
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const fields = React.useMemo<FormFieldSchema[]>(() => {
    return mapSchemaToFields(
      contactType?.customFieldsSchema || contact.contactType?.customFieldsSchema
    );
  }, [contactType, contact.contactType]);

  const { control, handleSubmit, reset } = useForm<FieldValues>({
    defaultValues: getCustomFieldDefaults(fields, contact as unknown as Record<string, unknown>),
  });

  React.useEffect(() => {
    reset(getCustomFieldDefaults(fields, contact as unknown as Record<string, unknown>));
  }, [contact, fields, reset]);

  const onSubmit = (data: Record<string, unknown>) => {
    const submission = prepareContactSubmission(fields, data);
    const fileAssociations = extractFileAssociations(fields, data).map((id) => ({
      fileId: id,
      category: 'custom-field',
    }));

    updateMutation.mutate({
      id: contact.id,
      ...submission,
      fileAssociations,
    });
  };

  if (!contact.typeId && !contact.contactType) return null;
  if (isLoadingType)
    return <Loader2 className="w-4 h-4 animate-spin mx-auto my-4 text-muted-foreground" />;
  if (fields.length === 0) return null;

  if (!isEditing && variant === 'readonly') {
    return (
      <div className="p-4 bg-slate-50 dark:bg-slate-900/60 border border-border rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {contactType?.label || contact.contactType?.label || 'Custom'} Attributes
          </h4>
          {!isReadOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[10px]"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>
        <div className="pt-1">
          <FormRenderer
            fields={fields}
            mode="view"
            values={getCustomFieldDefaults(fields, contact as unknown as Record<string, unknown>)}
            onGetFile={handleGetFile}
            onRenameFile={handleRenameFile}
          />
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="p-4 bg-slate-50 dark:bg-slate-900/60 border border-border rounded-xl space-y-4"
    >
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Edit {contactType?.label || contact.contactType?.label || 'Custom'} Attributes
        </h4>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => {
              if (onCancel) onCancel();
              else setIsEditing(false);
            }}
          >
            <X className="w-3 h-3" />
          </Button>
          <Button
            type="submit"
            size="sm"
            className="h-6 px-2 text-[10px] gap-1"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Save className="w-3 h-3" />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="pt-1">
        <FormRenderer
          fields={fields}
          mode="edit"
          control={control as unknown as Control<FieldValues>}
          onUploadFile={handleUploadFile}
          onGetFile={handleGetFile}
          onRenameFile={handleRenameFile}
        />
      </div>
    </form>
  );
}
